<template>
    <div class="overflow-x-auto">
        <div :style="{ minWidth: minWidthScroll }">
            <hts-card>
                <div class="grid grid-cols-4 gap-4 w-full px-4 lg:px-20 box-border">
                    <h4>
                        {{ $t('labels.vehiclesWithLoad') }}:
                    </h4>
                    <div class="col-span-3 text-center">
                        <div class="flex justify-around gap-4 items-center mb-2">
                            <p class="w-full">
                                {{ $t('labels.fromKg') }}
                            </p>
                            <p class="w-full">
                                {{ $t('labels.toUnderKg') }}
                            </p>
                            <div style="min-width: 60px" />
                        </div>
                        <div
                            v-for="(item, index) in data.vehicle"
                            :key="item">
                            <div
                                class="flex justify-around gap-4 items-center"
                                :class="{ 'mt-4': index > 0 }">
                                <hts-form-input
                                    v-model="item.from"
                                    class="w-full"
                                    readonly
                                    type="number" />
                                <hts-form-input
                                    v-model="item.to"
                                    class="w-full"
                                    type="number"
                                    readonly
                                    @change="(val) => changeForm(val, index)" />
                            </div>
                        </div>
                    </div>
                </div>
            </hts-card>
            <hts-card class="mt-20">
                <div class="grid grid-cols-4 gap-4 w-full px-4 lg:px-20 box-border">
                    <p class="text-lg font-bold">
                        {{ $t('labels.containerType') }}:
                    </p>
                    <div class="col-span-3 text-center">
                        <div
                            v-for="(item, index) in data.cont"
                            :key="item"
                            class="mb-5">
                            <div class="flex justify-around gap-4 items-center">
                                <hts-form-select
                                    v-model="data.cont[index]"
                                    :items="constCategory"
                                    class="w-full"
                                    readonly />
                            </div>
                        </div>
                    </div>
                </div>
            </hts-card>
        </div>
    </div>
</template>

<script setup lang="ts">
import { $lodash, $toast, $urlSystem } from '#imports'

import screen from '~/components/utils/screen'

onBeforeMount(async () => {
  await getData()
})
const breakpoint = screen()
const minWidthScroll = computed(() => {
  return `${breakpoint.value.thresholds.sm}px`
})

// Dữ liệu cho các cặp input
const data = ref<{
  vehicle: Array<{ from: number | undefined | null; to: number | undefined | null; id?: number | undefined | null }>
  cont: Array<number | undefined | null>
}>({
  vehicle: [
    { from: 0, to: undefined } // Cặp input mặc định
  ],
  cont: [null]
})

const addVehicleFields = () => {
  if (data.value.vehicle.length === 0) {
    data.value.vehicle.push({ from: 0, to: null })
  } else {
    let from = data.value.vehicle[data.value.vehicle.length - 1].to
    from = typeof from === 'number' ? from : null
    data.value.vehicle.push({ from, to: null })
  }
}

const changeForm = $lodash.debounce((load: number, index: number) => {
  if (data.value.vehicle.length >= index + 1) {
    data.value.vehicle[index + 1].from = load
  }
}, 300)

const removeVehicleField = (index: number) => {
  data.value.vehicle?.splice(index, 1)
  if (index !== 0 && index !== data.value.vehicle?.length) {
    data.value.vehicle[index].from = data.value.vehicle[index - 1].to
  }
}

const addContFields = () => {
  if (!data.value.cont || data.value.cont.length === 0) {
    data.value.cont = [null]
  } else {
    data.value.cont.push(null)
  }
}

const removeContField = (index: number) => {
  data.value.cont?.splice(index, 1)
}

const handleSave = async () => {
  if (!validateDataVehicle()) {
    process.client && $toast().error($t('labels.invalidVehicleLoad'))
    return
  }
  if (!validateDataCont()) {
    process.client && $toast().error($t('labels.invalidCont'))
    return
  }

  const api = $urlSystem.setting.tollStations.update
  const dataR: any = await $api(api, { body: data.value })
  const resData: any = dataR?.data?.value

  if (resData?.success) {
    process.client && $toast().success($t('labels.updateSuccess'))
  }
}

const validateDataVehicle = () => {
  const length = data.value.vehicle?.length
  if (length === 0) {
    return false
  }
  for (let i = 0; i < length; i++) {
    const item = data.value.vehicle?.[i]
    const { from, to } = item

    if (typeof from !== 'number') {
      return false
    }

    if (i < length - 1) {
      if (typeof to !== 'number') {
        return false
      }
      if (from >= to) {
        return false
      }
      if (to !== data.value.vehicle?.[i + 1].from) {
        return false
      }
    }

    if (i === length - 1) {
      if (typeof to === 'number') {
        if (from >= to) {
          return false
        }
      }
    }
  }

  return true
}

const validateDataCont = () => {
  if (data.value.cont.length === 0) {
    return true
  }

  if (data.value.cont.includes(null) || data.value.cont.includes(undefined) || data.value.cont.includes(0)) {
    return false
  }

  if (new Set(data.value.cont).size !== data.value.cont.length) {
    return false
  }

  return true
}

const getData = async () => {
  const api = $urlSystem.setting.tollStations.get
  const dataR: any = await $api(api)
  const resData: any = dataR?.data?.value

  if (resData.success) {
    // nếu from dòng đầu tiên đã lưu khác 0 thì cập nhật lại 0
    if (resData.data.vehicle && resData.data.vehicle.length > 0 && resData.data.vehicle[0].from) {
      resData.data.vehicle[0].from = 0
    }
    data.value.vehicle = resData.data.vehicle
    data.value.cont = resData.data.cont || [null]
  }
}

const constCategory = [
  {
    value: 20,
    label: $t('labels.vehicleModule.20ft')
  },
  {
    value: 40,
    label: $t('labels.vehicleModule.40ft')
  },
  {
    value: 45,
    label: $t('labels.vehicleModule.45ft')
  }
]
</script>
